import React from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * ChatBubble component displays a message bubble in the chat.
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Type of message ('user', 'assistant', or 'system')
 * @param {string} props.message - Message content
 * @param {string} props.time - Timestamp of the message
 * @param {number} props.messageId - Unique ID for the message
 * @param {number} props.activeTimeId - ID of the message currently showing time
 * @param {Function} props.setActiveTimeId - Function to set active time ID
 * @returns {JSX.Element} Chat bubble component
 */
const ChatBubble = ({ type, message, time, messageId, activeTimeId, setActiveTimeId, isNoInfoFound = false, isTyping = false, confidence = null, separateReferences = '', separateAttachments = '' }) => {
  const isUser = type === 'user';
  const isSystem = type === 'system';
  const showTime = activeTimeId === messageId;

  const toggleTime = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    if (showTime) {
      setActiveTimeId(null);
    } else {
      setActiveTimeId(messageId);
    }
  };



  // System messages have different styling and behavior
  if (isSystem) {
    return (
      <div
        className={`talkflow-chat-bubble system ${isNoInfoFound ? 'no-info-found' : ''}`}
        onClick={toggleTime}
        title="Nhấn để hiển thị thời gian"
      >
        <div className="talkflow-bubble-content">
          {message}
        </div>
        {time && showTime && (
          <div className="talkflow-bubble-time">
            {time}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`talkflow-chat-bubble ${isUser ? 'talkflow-user-bubble' : 'talkflow-assistant-bubble'} ${isTyping ? 'typing' : ''}`}
      onClick={toggleTime}
      title="Nhấn để hiển thị thời gian"
    >
      <div className="talkflow-bubble-content">
        {isTyping ? (
          <div className="talkflow-typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        ) : (
          <>
            <ReactMarkdown
              components={{
                // Fix nested p tags warning by using div instead of p for block content
                p: ({ children }) => {
                  // Handle empty paragraphs
                  const textContent = typeof children === 'string' ? children :
                    (Array.isArray(children) ? children.join('') : String(children));

                  if (!textContent.trim()) {
                    return <br />;
                  }

                  return (
                    <div style={{
                      marginBottom: '0.8em',
                      lineHeight: '1.6',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap' // Preserve whitespace and line breaks
                    }}>
                      {children}
                    </div>
                  );
                },
                // Better list formatting and indentation with proper spacing
                ul: ({ children }) => (
                  <ul style={{
                    marginBottom: '1em',
                    marginTop: '0.5em',
                    paddingLeft: '1.2em',
                    listStyle: 'disc outside',
                    lineHeight: '1.6',
                    whiteSpace: 'normal' // Reset any inherited whitespace
                  }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol style={{
                    marginBottom: '1em',
                    marginTop: '0.5em',
                    paddingLeft: '1.2em',
                    listStyle: 'decimal outside',
                    lineHeight: '1.6',
                    whiteSpace: 'normal' // Reset any inherited whitespace
                  }}>
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li style={{
                    marginBottom: '0.5em',
                    lineHeight: '1.6',
                    paddingLeft: '0.1em',
                    whiteSpace: 'normal', // Fixed: Use normal whitespace instead of pre-wrap
                    wordWrap: 'break-word',
                    display: 'list-item' // Ensure proper list item display
                  }}>
                    {children}
                  </li>
                ),
                // Enhanced emphasis styling with proper bold
                strong: ({ children }) => (
                  <strong style={{
                    fontWeight: '700', // Bolder weight
                    color: 'inherit'
                  }}>
                    {children}
                  </strong>
                ),
                // Handle em tags - convert to bold instead of italic for better Vietnamese readability
                em: ({ children }) => (
                  <span style={{
                    fontWeight: '700', // Make em tags bold
                    fontStyle: 'normal' // Remove italic styling 
                  }}>
                    {children}
                  </span>
                ),
                // Better header styling for Vietnamese
                h1: ({ children }) => (
                  <h1 style={{
                    fontSize: '1.3em',
                    fontWeight: '700',
                    marginBottom: '0.6em',
                    marginTop: '0.4em',
                    lineHeight: '1.4'
                  }}>
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 style={{
                    fontSize: '1.25em',
                    fontWeight: '700',
                    marginBottom: '0.5em',
                    marginTop: '0.4em',
                    lineHeight: '1.4'
                  }}>
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 style={{
                    fontSize: '1.15em',
                    fontWeight: '700',
                    marginBottom: '0.5em',
                    marginTop: '0.3em',
                    lineHeight: '1.4'
                  }}>
                    {children}
                  </h3>
                ),
                // Enhanced blockquote styling
                blockquote: ({ children }) => (
                  <blockquote style={{
                    borderLeft: '3px solid #e0e0e0',
                    paddingLeft: '1em',
                    margin: '0.75em 0',
                    fontStyle: 'italic',
                    color: '#666',
                    borderRadius: '0 4px 4px 0',
                    padding: '0.5em 1em'
                  }}>
                    {children}
                  </blockquote>
                ),
                // Enhanced code styling
                code: ({ children, className }) => {
                  if (className) {
                    // Code block
                    return (
                      <pre style={{
                        backgroundColor: '#f5f5f5',
                        padding: '0.75em',
                        borderRadius: '6px',
                        overflow: 'auto',
                        margin: '0.75em 0',
                        fontSize: '0.9em',
                        lineHeight: '1.4'
                      }}>
                        <code>{children}</code>
                      </pre>
                    );
                  }
                  // Inline code
                  return (
                    <code style={{
                      backgroundColor: '#f0f0f0',
                      padding: '0.15em 0.4em',
                      borderRadius: '3px',
                      fontSize: '0.9em',
                      border: '1px solid #e0e0e0'
                    }}>
                      {children}
                    </code>
                  );
                },
                // Enhanced link styling for attachments
                a: ({ children, href }) => (
                  <a
                    href={href}
                    style={{
                      color: '#0066cc',
                      textDecoration: 'none',
                      borderBottom: '1px solid #0066cc',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#f0f7ff';
                      e.target.style.borderBottomColor = '#0056b3';
                      e.target.style.color = '#0056b3';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderBottomColor = '#0066cc';
                      e.target.style.color = '#0066cc';
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                )
              }}
            >
              {message}
            </ReactMarkdown>

            {/* Render combined references and attachments section if available */}
            {(separateReferences || separateAttachments) && !isUser && (
              <div className="talkflow-references-section">
                {separateReferences && (
                  <ReactMarkdown
                    components={{
                      // Use same styling components as main content
                      strong: ({ children }) => (
                        <strong style={{
                          fontWeight: '700',
                          color: 'inherit'
                        }}>
                          {children}
                        </strong>
                      ),
                      ol: ({ children }) => (
                        <div style={{
                          marginBottom: '1em',
                          marginTop: '0.5em'
                        }}>
                          {children}
                        </div>
                      ),
                      li: ({ children }) => (
                        <div style={{
                          marginBottom: '0.5em',
                          lineHeight: '1.6',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }}>
                          {children}
                        </div>
                      )
                    }}
                  >
                    {separateReferences}
                  </ReactMarkdown>
                )}

                {separateAttachments && (
                  <ReactMarkdown
                    components={{
                      // Use same styling components as main content
                      strong: ({ children }) => (
                        <strong style={{
                          fontWeight: '700',
                          color: 'inherit'
                        }}>
                          {children}
                        </strong>
                      ),
                      ol: ({ children }) => (
                        <div style={{
                          marginBottom: '1em',
                          marginTop: '0.5em'
                        }}>
                          {children}
                        </div>
                      ),
                      li: ({ children }) => (
                        <div style={{
                          marginBottom: '0.5em',
                          lineHeight: '1.6',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }}>
                          {children}
                        </div>
                      ),
                      a: ({ children, href }) => (
                        <a
                          href={href}
                          style={{
                            color: '#0066cc',
                            textDecoration: 'none',
                            borderBottom: '1px solid #0066cc',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#f0f7ff';
                            e.target.style.borderBottomColor = '#0056b3';
                            e.target.style.color = '#0056b3';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderBottomColor = '#0066cc';
                            e.target.style.color = '#0066cc';
                          }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      )
                    }}
                  >
                    {separateAttachments}
                  </ReactMarkdown>
                )}
              </div>
            )}

            {confidence !== null && !isUser && !isSystem && confidence < 0.8 && (
              <div className="talkflow-confidence-indicator">
                <span className="confidence-icon">ℹ️</span>
                <span className="confidence-text">
                  Độ tin cậy: {Math.round(confidence * 100)}%
                </span>
              </div>
            )}
          </>
        )}
      </div>
      {time && showTime && (
        <div className="talkflow-bubble-time">
          {time}
        </div>
      )}
    </div>
  );
};

export default ChatBubble;
